import AreaSelection from "Components/AreaSelection/AreaSelection";
import ConfirmToLeave from "Components/Common/ConfirmToLeave/ConfirmToLeave";
import ContentView from "Components/ContentView/ContentView";
import DeleteModal from "Components/DeleteModal/DeleteModal";
import Loader from "Components/Loading/Loader/Loader";
import { Area } from "Models/Areas/Area";
import { AuditItemTemplate } from "Models/Audit/AuditItemTemplate";
import { AuditTemplate } from "Models/Audit/AuditTemplate";
import { ScoreType } from "Models/Audit/ScoreType";
import { DataState } from "Models/Common/DataState";
import { RoleType } from "Models/Users/RoleType";
import "rc-dialog/assets/index.css";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Action, Dispatch } from "redux";
import ServiceProvider from "ServiceProvider/ServiceProvider";
import Types from "ServiceProvider/Types";
import { IAuditTemplateAction } from "Store/AuditTemplates/AuditTemplateAction";
import { IAuditTemplateState } from "Store/AuditTemplates/AuditTemplateState";
import { IApplicationState } from "Store/Store";
import i18n from "Translations/i18n";
import { AuditItems } from "./AuditItems/AuditItems";
import AuditTemplateDetailHeader from "./AuditTemplateDetailHeader/AuditTemplateDetailHeader";
import styles from "./AuditTemplateDetailView.scss";
import { AuditTemplateInfo } from "./AuditTemplateInfo/AuditTemplateInfo";
import { AuditTemplateSettings } from "./AuditTemplateSettings/AuditTemplateSettings";

const auditTemplateAction = ServiceProvider.get<IAuditTemplateAction>(Types.IAuditTemplateAction);

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getAuditTemplateByIdAsync: (auditTemplateId: number) =>
        dispatch(auditTemplateAction.getAuditTemplateByIdAsync(auditTemplateId)),
    saveAuditTemplateAsync: (auditTemplate: AuditTemplate, pictureFile?: File, addAnother?: boolean) =>
        dispatch(auditTemplateAction.saveAuditTemplateAsync(auditTemplate, pictureFile, addAnother)),
    duplicateAuditTemplateAsync: (auditTemplate: AuditTemplate) =>
        dispatch(auditTemplateAction.duplicateAuditTemplateAsync(auditTemplate)),
    deleteAuditByIdAsync: (auditTemplateId: number) =>
        dispatch(auditTemplateAction.deleteAuditTemplateByIdAsync(auditTemplateId)),
    clearValidationErrorsByIdAsync: (auditTemplateId?: number) =>
        dispatch(auditTemplateAction.clearValidationErrorsByIdAsync(auditTemplateId))
});

const mapStateToProps = (state: IApplicationState) => ({
    auditTemplateState: state.auditTemplateState
});

interface IRouteParams {
    auditTemplateId: string; // must be type string since route params
}

interface IProps extends RouteComponentProps<IRouteParams> {
    auditTemplateState: IAuditTemplateState;
    saveAuditTemplateAsync: (auditTemplate: AuditTemplate, pictureFile?: File, descriptionFile?: File, videoFile?: File, videoThumbnailFile?: File, addAnother?: boolean) => Action;
    getAuditTemplateByIdAsync: (auditTemplateId: number) => Action;
    duplicateAuditTemplateAsync: (auditTemplate: AuditTemplate) => Action;
    clearValidationErrorsByIdAsync: (auditTemplateId?: number) => Action;
    deleteAuditByIdAsync: (auditTemplateId: number) => Action;
}

interface IState {
    auditTemplate?: AuditTemplate;
    isLoading: boolean;
    isOpenDeleteModal: boolean;
    pictureFile?: File;
    validationErrors: string[];
    confirmToLeave: boolean;
    isDuplicating: boolean;
}

export class AuditTemplateDetailView extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.loadAuditTemplate();
        this.auditTemplateUpdated = this.auditTemplateUpdated.bind(this);
        this.onInfoUpdated = this.onInfoUpdated.bind(this);
        this.onRoleChanged = this.onRoleChanged.bind(this);
        this.onSignatureRequiredChanged = this.onSignatureRequiredChanged.bind(this);
        this.loadAuditTemplate = this.loadAuditTemplate.bind(this);
        this.onAuditDeletedClicked = this.onAuditDeletedClicked.bind(this);
        this.closeDeleteDialog = this.closeDeleteDialog.bind(this);
        this.delete = this.delete.bind(this);
        this.performDelete = this.performDelete.bind(this);
        this.duplicate = this.duplicate.bind(this);
        this.save = this.save.bind(this);
        this.performSave = this.performSave.bind(this);
        this.saveAndAddAnother = this.saveAndAddAnother.bind(this);
        this.performSaveAndAddAnother = this.performSaveAndAddAnother.bind(this);
        this.onItemsUpdated = this.onItemsUpdated.bind(this);
        this.onAreaSelectionChanged = this.onAreaSelectionChanged.bind(this);
        this.onScoreTypeChanged = this.onScoreTypeChanged.bind(this);
        this.onScoreSelectionChanged = this.onScoreSelectionChanged.bind(this);
    }

    private loadAuditTemplate() {
        const templateIdParam = this.props.match.params.auditTemplateId;
        if (templateIdParam && templateIdParam !== "new") {
            const auditTemplateId = parseInt(this.props.match.params.auditTemplateId, 0);
            this.props.getAuditTemplateByIdAsync(auditTemplateId);
            this.state = {
                isLoading: true,
                isOpenDeleteModal: false,
                validationErrors: [],
                confirmToLeave: false,
                isDuplicating: false
            };
            this.props.clearValidationErrorsByIdAsync(auditTemplateId);
        }
        else {
            this.state = {
                isLoading: false,
                isOpenDeleteModal: false,
                auditTemplate: new AuditTemplate(),
                validationErrors: [],
                confirmToLeave: false,
                isDuplicating: false
            };
            this.props.clearValidationErrorsByIdAsync();
        }
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        let auditTemplateId: number = -1;
        const partialState: Partial<IState> = {};

        if (nextProps.match.params.auditTemplateId && nextProps.match.params.auditTemplateId !== "new") {
            auditTemplateId = parseInt(nextProps.match.params.auditTemplateId, 0);

            if (
                nextProps.auditTemplateState.detailAuditTemplates &&
                nextProps.auditTemplateState.detailAuditTemplates[auditTemplateId] &&
                (
                    !prevState.auditTemplate ||
                    !prevState.auditTemplate.id ||
                    nextProps.auditTemplateState.detailAuditTemplates[auditTemplateId].lastUpdatedAt !== prevState.auditTemplate.lastUpdatedAt
                )
            ) {
                partialState.auditTemplate = nextProps.auditTemplateState.detailAuditTemplates[auditTemplateId];
                partialState.confirmToLeave = false;

                if (prevState.isDuplicating) {
                    partialState.isDuplicating = false;
                }
            }
            if (nextProps.auditTemplateState.detailAuditTemplateStates) {
                const templateState = nextProps.auditTemplateState.detailAuditTemplateStates[auditTemplateId];
                if (templateState === DataState.Deleted) {
                    nextProps.history.push("/audittemplates");
                }
                else if ((templateState === DataState.Loading) !== prevState.isLoading) {
                    partialState.isLoading = templateState === DataState.Loading;
                }
            }
        }

        if (nextProps.auditTemplateState.detailAuditTemplateValidationErrors
            && nextProps.auditTemplateState.detailAuditTemplateValidationErrors[auditTemplateId]
            && nextProps.auditTemplateState.detailAuditTemplateValidationErrors[auditTemplateId] !== prevState.validationErrors) {
            partialState.validationErrors = nextProps.auditTemplateState.detailAuditTemplateValidationErrors[auditTemplateId];
        }

        return partialState;
    }

    public auditTemplateUpdated() {
        this.setState({ confirmToLeave: true });
    }

    public onInfoUpdated(name: string, isMediaChanged: boolean, pictureFile?: File) {
        let template = this.state.auditTemplate;
        if (isMediaChanged) {
            template = {
                ...template!,
                pictureUrl: pictureFile ? URL.createObjectURL(pictureFile) : undefined
            };
        }

        this.setState({
            auditTemplate: {
                ...template!,
                name
            },
            pictureFile
        });
        this.auditTemplateUpdated();
    }

    public onRoleChanged(newRole: RoleType) {
        this.setState({
            auditTemplate: {
                ...this.state.auditTemplate!,
                role: newRole
            }
        });
        this.auditTemplateUpdated();
    }

    public onSignatureRequiredChanged(signatureRequired: boolean, doubleSignatureRequired: boolean) {
        this.setState({
            auditTemplate: {
                ...this.state.auditTemplate!,
                signatureRequired,
                doubleSignatureRequired
            }
        });
        this.auditTemplateUpdated();
    }

    public onScoreTypeChanged(newScoreType: ScoreType) {
        this.setState({
            auditTemplate: {
                ...this.state.auditTemplate!,
                scoreType: newScoreType
            }
        });
        this.auditTemplateUpdated();
    }

    public onScoreSelectionChanged(newMinValue: number, newMaxValue: number) {
        this.setState({
            auditTemplate: {
                ...this.state.auditTemplate!,
                minTaskScore: newMinValue,
                maxTaskScore: newMaxValue
            }
        });
        this.auditTemplateUpdated();
    }

    public onAuditDeletedClicked() {
        this.setState({
            isOpenDeleteModal: true
        });
    }

    public closeDeleteDialog() {
        this.setState({
            isOpenDeleteModal: false
        });
    }

    public delete() {
        if (this.state.auditTemplate && this.state.auditTemplate.id) {
            this.setState({ confirmToLeave: false });
            window.setTimeout(this.performDelete);
        }
    }

    public performDelete() {
        if (this.state.auditTemplate && this.state.auditTemplate.id) {
            this.props.deleteAuditByIdAsync(this.state.auditTemplate.id);
        }
    }

    public duplicate() {
        if (this.state.auditTemplate) {
            this.setState({ isDuplicating: true });
            this.props.duplicateAuditTemplateAsync(this.state.auditTemplate);
        }
    }

    public save() {
        if (this.state.auditTemplate) {
            this.setState({ confirmToLeave: false });
            window.setTimeout(this.performSave);
        }
    }

    private performSave() {
        if (this.state.auditTemplate) {
            this.props.saveAuditTemplateAsync(this.state.auditTemplate, this.state.pictureFile);
        }
    }

    public saveAndAddAnother() {
        if (this.state.auditTemplate) {
            this.setState({ confirmToLeave: false });
            window.setTimeout(this.performSaveAndAddAnother);
        }
    }

    private performSaveAndAddAnother() {
        if (this.state.auditTemplate) {
            this.props.saveAuditTemplateAsync(this.state.auditTemplate, this.state.pictureFile, undefined, undefined, undefined, true);
            this.setState({
                auditTemplate: { ...this.state.auditTemplate, id: undefined }
            });
        }
    }

    public onItemsUpdated(items: AuditItemTemplate[]) {
        this.setState({
            auditTemplate: {
                ...this.state.auditTemplate!,
                items,
            },
        });
        this.auditTemplateUpdated();
    }

    public onAreaSelectionChanged(selectedArea?: Area) {
        this.setState({
            auditTemplate: {
                ...this.state.auditTemplate!,
                area: selectedArea
            }
        });
        this.auditTemplateUpdated();
    }

    public render() {
        return (
            <div id="divAuditTemplateDetailView">
                {this.state.auditTemplate ? (
                    <>
                        <ConfirmToLeave confirmToLeave={this.state.confirmToLeave} />
                        <AuditTemplateDetailHeader canDuplicate={!this.state.confirmToLeave} isDuplicating={this.state.isDuplicating} onAuditDeletedClicked={this.onAuditDeletedClicked} duplicate={this.duplicate} save={this.save} saveAndAddAnother={this.saveAndAddAnother} auditTemplate={this.state.auditTemplate} />
                        <ContentView className={`${styles.validationErrors} ${this.state.validationErrors && this.state.validationErrors.length ? styles.visible : styles.hidden}`}>
                            <div className={styles.error}>
                                <h2>{i18n.t("audits.validationErrors.foundErrors")}</h2>
                                <ul>
                                    {
                                        this.state.validationErrors.map((validationError, index) => {
                                            return (<li key={validationError + "-" + index}>{i18n.t(`audits.validationErrors.${validationError}`)}</li>);
                                        })
                                    }
                                </ul>
                            </div>
                        </ContentView>
                        <AuditTemplateInfo auditTemplate={this.state.auditTemplate} onChange={this.onInfoUpdated} />
                        <AuditItems auditTemplate={this.state.auditTemplate} onChange={this.onItemsUpdated} />
                        <AreaSelection className={styles.areaSearch} backgroundIsGray={true} selectedArea={this.state.auditTemplate.area} onSelectionChanged={this.onAreaSelectionChanged} showAreaNavigator={true} title={i18n.t("audits.area.title")} />
                        <AuditTemplateSettings
                            selectedRole={this.state.auditTemplate.role}
                            selectedSingleSignatureRequired={this.state.auditTemplate.signatureRequired}
                            selectedDoubleSignatureRequired={this.state.auditTemplate.doubleSignatureRequired}
                            selectedScoreType={this.state.auditTemplate.scoreType}
                            minCustomScoreValue={this.state.auditTemplate.minTaskScore}
                            maxCustomScoreValue={this.state.auditTemplate.maxTaskScore}
                            onRoleChanged={this.onRoleChanged}
                            onSignatureRequiredChanged={this.onSignatureRequiredChanged}
                            onScoreTypeChanged={this.onScoreTypeChanged}
                            onScoreSelectionChanged={this.onScoreSelectionChanged}
                            auditHasBeenCompleted={this.state.auditTemplate.lastCompletedDate ? true : false}
                        />
                        <DeleteModal visible={this.state.isOpenDeleteModal} onDelete={this.delete} onCancel={this.closeDeleteDialog} message={i18n.t("audits.confirmDeleteText")} />
                    </>
                ) : this.state.isLoading ? (
                    <Loader />
                ) : (
                            <span id="spnAuditTemplateNotFound">{i18n.t("audits.auditTemplateNotFound")}</span>
                        )}
            </div>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(AuditTemplateDetailView);


// WEBPACK FOOTER //
// ./src/Components/Pages/AuditTemplateDetailView/AuditTemplateDetailView.tsx